import { useTranslations } from 'use-intl';
import type { Explained } from '@/lib/loadClient';

/**
 * "How is this calculated?", answered by the code that calculated it.
 *
 * `CLAUDE.md` has required this from the start and the engine has always
 * obliged — every function in `worker/domain/` returns
 * `{ value, formula, inputs, terms }`, and D-016 gives the reason: "a tooltip
 * written separately from the arithmetic drifts from it the first time either
 * changes". What was missing was anywhere to read it. `why.howCalculated`
 * existed in all three dictionaries and was used by nothing; the only formula a
 * coach could see was one line hand-written on the session screen.
 *
 * It renders the formula sentence and the ordered terms. It deliberately does
 * **not** render `inputs`: those keys are `totalMinutes`, `chronicWeekly`,
 * `sessionRpe` — field names, not words, and a coach has already been handed a
 * PDF with `minutes.weekObjective` on it. The terms carry translation keys and
 * say the same thing in the product's own vocabulary.
 *
 * Numbers are printed exactly as the engine gives them. A term's `delta` means
 * different things in different formulas — a base, a factor, a signed
 * adjustment — so decorating them with "+" would be the UI inventing a claim
 * the engine never made. The formula sentence above them carries the meaning.
 *
 * A disclosure rather than a popover: it is read once, it has to work on a
 * phone, and `<details>` gets keyboard and screen-reader behaviour for free.
 */
export function Formula({ explained, className = '' }: { explained: Explained; className?: string }) {
  const t = useTranslations();
  if (!explained.formula) return null;

  return (
    <details className={`group ${className}`}>
      <summary className="inline-flex min-h-11 cursor-pointer list-none items-center text-meta text-ink-muted underline decoration-line-strong underline-offset-2 transition-colors hover:text-ink hover:decoration-ink">
        {t('why.howCalculated')}
      </summary>

      <div className="mt-1 border-s-2 border-line ps-3">
        <p className="max-w-[60ch] text-meta">{t(explained.formula)}</p>

        {explained.terms.length > 0 ? (
          <ul className="mt-2 grid gap-1">
            {explained.terms.map((term, i) => (
              <li
                key={`${term.key}-${i}`}
                className="flex flex-wrap items-baseline justify-between gap-x-3 text-meta text-ink-muted"
              >
                <span>{t(term.key)}</span>
                <span className="tabular text-ink">{term.delta}</span>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </details>
  );
}
